import { useCallback, useEffect, useRef } from 'react';
import { mintRealtimeSession } from '../api/realtimeApi';

export default function useRealtimeWebRTC({
  onTranscript,
  onResponse,
  onSessionReady,
  onEndProposed,
  onTrainingToolCall,
  onResponseCreated,
  onResponseDone,
  onRealtimeError,
  onVolume,
  onRemoteVolume,
  onRealtimeEvent,
  
}) {
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const remoteAudioElRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const readyRef = useRef(false);
  const mutedRef = useRef(false);

  // --- INBOUND AUDIO PROBE ---
  let recvProbe = useRef(null);

  function stopInboundProbe() {
    if (recvProbe.current) clearInterval(recvProbe.current);
    recvProbe.current = null;
  }
  // ---

  // function-call streaming buffers
  const funcArgBuffersRef = useRef({});
  const funcMetaRef = useRef({});

  // mic meter
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const meterIdRef = useRef(null);
  const remoteAudioCtxRef = useRef(null);
  const remoteAnalyserRef = useRef(null);
  const remoteMeterIdRef = useRef(null);
  const connectingRef = useRef(false);
  const stopMeter = useCallback(() => {
    if (meterIdRef.current) cancelAnimationFrame(meterIdRef.current);
    meterIdRef.current = null;
    try { audioCtxRef.current?.close(); } catch { }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  const startMeter = useCallback((stream) => {
    stopMeter();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        try { onVolume?.(rms); } catch { }
        meterIdRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch { }
  }, [onVolume, stopMeter]);

  const stopRemoteMeter = useCallback(() => {
    if (remoteMeterIdRef.current) cancelAnimationFrame(remoteMeterIdRef.current);
    remoteMeterIdRef.current = null;
    try { remoteAudioCtxRef.current?.close?.(); } catch { }
    remoteAudioCtxRef.current = null;
    remoteAnalyserRef.current = null;
    try { onRemoteVolume?.(0); } catch {}
  }, [onRemoteVolume]);

  const startRemoteMeter = useCallback((stream) => {
    stopRemoteMeter();
    if (!stream || !stream.getAudioTracks?.().length) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      remoteAudioCtxRef.current = ctx;

      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.72;
      src.connect(analyser);
      remoteAnalyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        try { onRemoteVolume?.(rms); } catch {}
        remoteMeterIdRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {}
  }, [onRemoteVolume, stopRemoteMeter]);

  function waitForIceGatheringComplete(pc, timeoutMs = 2000) {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      const onChange = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', onChange);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', onChange);
      setTimeout(() => {
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      }, timeoutMs);
    });
  }

  const setAudioElement = useCallback((el) => {
    remoteAudioElRef.current = el || null;
    console.log('[setAudioElement]', { el });
    if (!el) return;
    try {
      el.setAttribute('playsinline', '');
      el.muted = false;
      el.volume = 1;
    } catch {}
    if (remoteStreamRef.current) {
      el.srcObject = remoteStreamRef.current;
      console.log('[setAudioElement] assigned stream!', remoteStreamRef.current);
    }
    try { el.play?.(); } catch {}
    const unlock = () => el.play?.().catch(()=>{});
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown',     unlock, { once: true });
  }, []);

  const sendEvent = useCallback((payload) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open'){
      console.warn("[RealtimeWebRTC] DataChannel not open; dropping event:", payload?.type);
      return false;
    } 
    try { dc.send(JSON.stringify(payload)); } catch(e) {
      console.error("[RealtimeWebRTC] Failed to send:", e);
      return false;
     }
    return true;
  }, []);

  const handleFunctionCallDone = useCallback((callId, functionName, rawArgs = '') => {
    if (!callId || !functionName) return;

    const meta = funcMetaRef.current[callId] || {};
    if (meta.handled) return;
    funcMetaRef.current[callId] = { ...meta, name: functionName, handled: true };
    delete funcArgBuffersRef.current[callId];

    let args = {};
    try { args = rawArgs ? JSON.parse(rawArgs) : {}; } catch { }

    if (functionName === 'propose_session_end') {
      console.log("[Realtime] propose_session_end detected, firing onEndProposed()");
      try { onEndProposed?.(args); } catch (err) {
        console.error("[Realtime] onEndProposed threw error:", err);
      }
      return;
    }

    if (functionName === 'evaluate_progress_and_advance' || functionName === 'generate_next_scenario') {
      console.log('[TrainingTool] function call arguments done', {
        callId,
        name: functionName,
        args,
      });
      onTrainingToolCall?.(callId, functionName, args);
    }
  }, [onEndProposed, onTrainingToolCall]);

  const setMicEnabled = useCallback(async (enabled) => {
    const stream = localStreamRef.current;
    if (!stream) return;
    for (const t of stream.getAudioTracks()) t.enabled = !!enabled;
  }, []);

  const getLocalStream = useCallback(() => localStreamRef.current, []);
  const getRemoteStream = useCallback(() => remoteStreamRef.current, []);

  const teardown = useCallback(() => {
    stopMeter();
    stopInboundProbe();
    stopRemoteMeter();
    try { dcRef.current?.close(); } catch { }
    try { pcRef.current?.getSenders().forEach(s => { try { s.track?.stop(); } catch { } }); } catch { }
    try { pcRef.current?.close(); } catch { }
    pcRef.current = null;
    dcRef.current = null;
    localStreamRef.current?.getTracks().forEach(t => { try { t.stop(); } catch { } });
    localStreamRef.current = null;
    readyRef.current = false;
    connectingRef.current = false;
  }, [stopMeter, stopRemoteMeter]);

  const connect = useCallback(async (sessionDefaults = {}, preGrantedStream = null, options = {}) => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    teardown();

    // 1) Mint ephemeral token
    const session = await mintRealtimeSession({
      trainingPlanId: options?.trainingPlanId ?? null,
    });
    const ephem = session?.client_secret?.value || session?.session?.client_secret?.value || session?.value;
    if (!ephem) throw new Error('No client_secret returned');

    // 2) Create RTCPeerConnection
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    pcRef.current = pc;
    pc.ontrack = (e) => {
        const inbound = e.streams[0];
        remoteStreamRef.current = inbound;
        console.log('[ontrack] got inbound stream', inbound);
        startRemoteMeter(inbound);
      
        let el = remoteAudioElRef.current;
        if (!el) {
          el = document.getElementById('assistant-audio');
          remoteAudioElRef.current = el;
        }
        if (el) {
          el.srcObject = inbound;
          el.muted = false;
          el.volume = 1;
          el.play?.().catch(err => console.log('audio.play blocked:', err?.message));
        } else {
          console.log('[ontrack] STILL no audio element!');
        }
      // One-time user-gesture retry for Safari/autoplay
      const once = () => {
        const a = remoteAudioElRef.current;
        if (a) { a.muted = false; a.volume = 1; a.play?.().catch(() => { }); }
        document.removeEventListener('click', once);
      };
      document.addEventListener('click', once, { once: true });
    };

    // 3) Create data channel for events
    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;

    dc.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }

      try { onRealtimeEvent?.(msg); } catch { }

      switch (msg.type) {
        case 'session.created':
          readyRef.current = true;
          try { onSessionReady?.(); } catch { }
          break;
        case 'response.created':
          try { onResponseCreated?.(msg); } catch { }
          break;
        case 'response.output_item.added': {
          const it = msg.item;
          if (it?.type === 'function_call' && it.call_id) {
            funcMetaRef.current[it.call_id] = { name: it.name, handled: false };
            if (
              it.name === 'evaluate_progress_and_advance' ||
              it.name === 'generate_next_scenario'
            ) {
              console.log('[TrainingTool] function call item added', {
                callId: it.call_id,
                name: it.name,
              });
            }
          }
          break;
        }
        case 'response.function_call_arguments.delta': {
          const { call_id, delta } = msg;
          if (call_id && typeof delta === 'string') {
            funcArgBuffersRef.current[call_id] =
              (funcArgBuffersRef.current[call_id] || '') + delta;
          }
          break;
        }
        case 'response.function_call_arguments.done': {
          const { call_id } = msg;
          const meta = funcMetaRef.current[call_id] || {};
          const raw =
            funcArgBuffersRef.current[call_id] ||
            (typeof msg.arguments === 'string' ? msg.arguments : '');
          handleFunctionCallDone(call_id, meta.name || msg.name || msg.item?.name || '', raw);
          break;
        }
        case 'response.output_item.done': {
          const item = msg.item;
          if (item?.type !== 'function_call') break;
          const callId = String(item.call_id || '');
          if (!callId) break;
          const meta = funcMetaRef.current[callId] || {};
          if (meta.handled) break;
          const functionName = String(item.name || meta.name || '');
          if (!functionName) break;

          const rawArgs = (() => {
            if (typeof item.arguments === 'string') return item.arguments;
            if (item.arguments && typeof item.arguments === 'object') {
              try {
                return JSON.stringify(item.arguments);
              } catch {
                return '';
              }
            }
            return funcArgBuffersRef.current[callId] || '';
          })();

          handleFunctionCallDone(callId, functionName, rawArgs);
          break;
        }
        
        case 'response.content_part.done':
          if (mutedRef.current) break;
          if (msg.part?.type === 'text' && typeof msg.part.text === 'string') {
            try { onResponse?.(msg.part.text); } catch { }
          }
          break;
        case 'conversation.item.input_audio_transcription.completed':
          try { onTranscript?.(msg.transcript || '', msg); } catch { }
          break;
        case 'conversation.item.input_audio_transcription.failed':
          try { onTranscript?.(''); } catch { }
          break;
        case 'response.audio_transcript.done':
        case 'response.output_audio_transcript.done':
        case 'response.output_text.done':
          if (mutedRef.current) break;
          try { onResponse?.(msg.transcript || msg.text || ''); } catch { }
          break;
        case 'response.output_audio.delta':
        case 'response.output_audio_transcript.delta':
        case 'response.output_text.delta':
          break;
        case 'response.done':
        case 'response.cancelled':
          try { onResponseDone?.(msg); } catch { }
          break;
        case 'response.error':
            console.log('[RT end]', msg.type, msg.error || '');
            try { onRealtimeError?.(msg.error || msg); } catch { }
            break;
      
        case 'error':
            if (!(msg.error?.code === 'response_cancel_not_active')) {
                console.warn('RT error', msg.error);
              }
            try { onRealtimeError?.(msg.error || msg); } catch { }
          break;
        default:
          break;
      }
    };

    // 4) Add mic on a single sendrecv transceiver (one m-line)
    const stream = preGrantedStream || await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true }
    });
    localStreamRef.current = stream;
    const micTrack = stream.getAudioTracks()[0];
    micTrack.enabled = !options?.pushToTalk;
    const tx = pc.addTransceiver('audio', { direction: 'sendrecv' });
    await tx.sender.replaceTrack(micTrack);
    startMeter(stream);

    console.log('[pc] state before offer:', pc.signalingState);
    const offer = await pc.createOffer();
    console.log('[pc] after createOffer:', pc.signalingState);
    await pc.setLocalDescription(offer);
    console.log('[pc] after setLocalDescription(offer):', pc.signalingState);
    await waitForIceGatheringComplete(pc);
    if (pcRef.current !== pc) throw new Error('Peer connection replaced before setLocalDescription');
    const localSdp = pc.localDescription?.sdp || offer.sdp;

    const sdpResp = await fetch('https://api.openai.com/v1/realtime/calls', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${ephem}`,
        'Content-Type': 'application/sdp'
      },
      body: localSdp
    });

    pc.oniceconnectionstatechange = () => console.log('[pc] ice:', pc.iceConnectionState);
    pc.onconnectionstatechange   = () => console.log('[pc] conn:', pc.connectionState);
    if (!sdpResp.ok) {
      const errText = await sdpResp.text().catch(() => '');
      throw new Error(`SDP exchange failed: ${sdpResp.status} ${errText}`);
    }
    const answer = await sdpResp.text();
    if (!pcRef.current || pcRef.current !== pc || pc.signalingState === 'closed') {
      throw new Error('Peer connection closed/replaced before setRemoteDescription');
    }
    if (pc.signalingState !== 'have-local-offer') {
      throw new Error(`Expected have-local-offer before SRD, got ${pc.signalingState}`);
    }
    await pc.setRemoteDescription({ type: 'answer', sdp: answer });


    // Apply session defaults after data channel opens (small delay ok)
    dc.onopen = () => {
      if (sessionDefaults && Object.keys(sessionDefaults).length) {
        dc.send(JSON.stringify({ type: 'session.update', session: sessionDefaults }));
      }
      readyRef.current = true;
      try { onSessionReady?.(); } catch { }
    };
    connectingRef.current = false;
    return pc;
  }, [
    onResponse,
    onResponseCreated,
    onResponseDone,
    onRealtimeError,
    onRealtimeEvent,
    onSessionReady,
    onTranscript,
    handleFunctionCallDone,
    startMeter,
    startRemoteMeter,
    teardown,
  ]);

  const disconnect = useCallback(() => {
    teardown();
  }, [teardown]);

  useEffect(() => () => teardown(), [teardown]);

  return {
    connect,
    disconnect,
    sendEvent,
    setMicEnabled,
    setAudioElement,
    getLocalStream,
    getRemoteStream,
    // haltOutputs,
    // unhaltOutputs,
    ready: readyRef.current,
  };
}
